// tip-selection.component.ts
import {Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import { CommonModule } from '@angular/common';
import MoneyUtils from '../../utils/Utils';
import {TipButtonComponent} from '../buttons/tip-button.component';
import confetti from 'canvas-confetti';
import {TranslateService} from '../../services/translate.service';
import {ActiveTipConfig} from '../../models/active-tip-config-model';

interface TipButton {
  id: string;
  text: string;
}

export interface TipChangeEvent {
  buttonId: string;
  amount: number;
}

@Component({
  selector: 'app-tip-selection',
  standalone: true,
  imports: [CommonModule, TipButtonComponent],
  template: `
    <div class="flex flex-row mt-4">
      <div class="flex-1 text-lg">{{title}}</div>
      <div class="text-lg text-center tabular-nums">{{tipAmountFormatted}}</div>
    </div>

    <div class="flex justify-evenly mt-8">
      <app-tip-button
        *ngFor="let btn of tipButtons; let i = index"
        [text]="btn.text"
        [selected]="selectedTip === btn.id"
        [attention]="needsAttention"
        [animationDelay]="i * 0"
        (onClick)="selectTip(btn.id, $event)">
      </app-tip-button>
    </div>
  `
})
export class TipSelectionComponent implements OnInit, OnChanges {
  tipAmountFormatted = '';
  @Input() selectedTip = '0';
  @Input() amount = 0;
  @Output() onTipChange = new EventEmitter<TipChangeEvent>();
  @Input() config?: ActiveTipConfig;
  private tipAmount: number = 0;
  private destroyed = false;
  private confettiInstance: any;
  tipButtons: TipButton[] = [];
  title: string = "";

  get needsAttention(): boolean {
    return this.selectedTip === 'not_selected';
  }

  constructor(private translate: TranslateService) {}

  ngOnInit() {
    this.calculateTipAmount();

    this.tipButtons = [
      { id: '0', text: this.getString('Ingen') },
    ];

    if (this.config) {
      const l1 = {id: this.config.tipLevel1.toString(), text: `${this.config.tipLevel1}%`};
      const l2 = {id: this.config.tipLevel2.toString(), text: `${this.config.tipLevel2}%`};
      const l3 = {id: this.config.tipLevel3.toString(), text: `${this.config.tipLevel3}%`};
      this.tipButtons.push(l1);
      this.tipButtons.push(l2);
      this.tipButtons.push(l3);
    }

    this.updateTitle();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-base the selected tip percentage when the amount changes (e.g. a discount was applied),
    // so the tip never stays computed on a stale amount. Skip forced selection — emitting there
    // would count as a choice the guest never made.
    const amountChange = changes['amount'];
    if (amountChange && !amountChange.firstChange && this.selectedTip !== 'not_selected') {
      this.calculateTipAmount();
      // Emit async — a sync emit would mutate parent state mid change-detection (NG0100).
      // The destroyed guard covers the case where the same change also removes this component
      // from the DOM (e.g. the bill became free and the tip section is hidden).
      queueMicrotask(() => {
        if (!this.destroyed) {
          this.onTipChange.emit({ buttonId: this.selectedTip, amount: this.tipAmount });
        }
      });
    }
  }

  updateTitle() {
    if (this.selectedTip == 'not_selected') {
      this.title = this.getString("Välj dricksbelopp");
      return;
    }

    this.title = this.getString("Dricks");
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.onTipChange.unsubscribe();
    if (this.confettiInstance) {
      this.confettiInstance.reset();
    }
  }

  getString(key: string): string {
    return this.translate.getString(key);
  }

  selectTip(id: string, event: MouseEvent): void {
    if (this.selectedTip !== id && id !== '0') {
      this.triggerConfetti(id, event);
    }
    this.selectedTip = id;
    this.calculateTipAmount();
    this.updateTitle();
    this.onTipChange.emit({ buttonId: id, amount: this.tipAmount });
  }

  triggerConfetti(id: string, event: MouseEvent): void {
    const button = event.target as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;

    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.left = '0';
    canvas.style.top = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    document.body.appendChild(canvas);

    this.confettiInstance = confetti.create(canvas, { resize: true });
    this.confettiInstance({
      particleCount: Number(id) * 10,
      spread: 40,
      origin: {
        x: buttonCenterX / window.innerWidth,
        y: buttonCenterY / window.innerHeight
      },
    });
  }

  private calculateTipAmount() {
    if (this.selectedTip === 'not_selected') {
      this.tipAmount = 0;
      this.tipAmountFormatted = "";
      return;
    }
    const percent = parseInt(this.selectedTip);
    const a = this.amount;
    console.log(`Amount: ${a}`);
    this.tipAmount = a ? Math.ceil(a * percent / 100) : 0;
    this.tipAmountFormatted = MoneyUtils.formatNoCents(this.tipAmount);
  }

}
